import { useCurrentFrame, interpolate } from 'remotion';
import { CSSProperties, ReactNode } from 'react';

// Constants
import { CLAMP_BOTH } from '@/constants';

// Utils
import { combineClasses } from '@/utils';

export interface TypingTextProps {
  /** The text content to animate with typing effect */
  text: string;
  /** Optional CSS class names */
  className?: string;
  /** Frame number when animation should start */
  startFrame: number;
  /** Number of characters to type per frame (default: 1 = 1 frame per character) */
  charsPerFrame?: number;
  /** Whether to show a blinking cursor at the end (default: true) */
  showCursor?: boolean;
  /** Cursor blink speed in frames (default: 12) */
  cursorBlinkSpeed?: number;
  /** Cursor color (default: 'currentColor') */
  cursorColor?: string;
  /** Untyped portion is transparent to keep layout stable */
  stableLayout?: boolean;
  /** Optional inline styles */
  style?: CSSProperties;
}

/**
 * TypingText - Typewriter Animation Effect
 *
 * Animates text character by character like a typewriter.
 * Features include:
 * - Configurable typing speed
 * - Realistic blinking cursor (thin vertical bar like text editors)
 * - Smooth animation with Remotion's frame-based timing
 * - Cursor glows subtly while typing
 */
export const TypingText = ({
  text,
  className = '',
  startFrame,
  charsPerFrame = 2,
  showCursor = true,
  cursorBlinkSpeed = 12,
  cursorColor = 'currentColor',
  stableLayout = false,
  style,
}: TypingTextProps): ReactNode => {
  const frame = useCurrentFrame();

  // Calculate how many characters should be visible at the current frame
  const elapsedFrames = Math.max(0, frame - startFrame);
  const totalChars = text.length;

  // Calculate visible characters count
  const visibleCharsCount = Math.min(
    totalChars,
    Math.floor(elapsedFrames * charsPerFrame),
  );

  // Get the visible portion of text
  const visibleText = text.slice(0, visibleCharsCount);

  // Check if typing is complete
  const isTypingComplete = visibleCharsCount >= totalChars;

  // Cursor opacity with smooth blink effect
  // While typing: always visible with subtle pulse
  // After typing: classic blink effect
  const blinkCycle = (frame - startFrame) / cursorBlinkSpeed;
  const cursorOpacity = isTypingComplete
    ? // After complete: sharp blink (on/off)
      Math.floor(blinkCycle) % 2 === 0
      ? 1
      : 0.2
    : // While typing: subtle pulse to feel alive
      0.8 + Math.sin(blinkCycle * Math.PI * 2) * 0.2;

  // Container opacity - fade in at start
  const containerOpacity = interpolate(
    frame,
    [startFrame, startFrame + 5],
    [0, 1],
    CLAMP_BOTH,
  );

  // Cursor glow intensity (more visible while typing)
  const cursorGlow = isTypingComplete ? 0 : 4;

  return (
    <span
      className={combineClasses(className, 'inline')}
      style={{
        opacity: containerOpacity,
        ...style,
      }}
    >
      {visibleText}

      {stableLayout && (
        <span className="text-transparent">
          {text.slice(visibleCharsCount)}
        </span>
      )}

      {showCursor && (
        <span
          className="inline-block align-baseline h-0.5 ml-2.5 rounded-sm w-5"
          style={{
            backgroundColor: cursorColor,
            opacity: cursorOpacity,
            boxShadow:
              cursorGlow > 0 ? `0 0 ${cursorGlow}px ${cursorColor}` : 'none',
            transition: isTypingComplete ? 'opacity 0.05s' : 'none',
          }}
        />
      )}
    </span>
  );
};
